import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CrudPage } from '@/components/CrudPage';
import { Badge, Button, Card, Modal, Toast } from '@/components/ui';
import { AgentLoginList } from '@/components/AgentLoginResults';
import {
  AsteriskStateBadge,
  ASTERISK_STATE_HELP,
} from '@/components/AsteriskState';
import { formatRelative } from '@/lib/format';
import { useDialers } from '@/lib/dialers';
import type { Dialer } from '@/lib/dialers';

interface TestSyncResult {
  connection: { ok: boolean; mock: boolean; detail: string; version: string | null };
  agents: Array<{ vicidial_user: string; state: string; detail: string }>;
  agents_total: number;
  agents_ok: number;
  ok: boolean;
  integration: string | null;
}

interface PingResult {
  integration_id?: string;
  integration: string | null;
  integration_code?: string | null;
  ok: boolean;
  mock_mode: boolean;
  version: string | null;
  error: string | null;
}

interface TransferTest {
  ok: boolean;
  ready: boolean;
  /** `passed: null` means "cannot be checked from here", not "failed". */
  checks: { name: string; passed: boolean | null; detail: string }[];
  note?: string;
}

export default function VicidialServers() {
  const queryClient = useQueryClient();
  const { data: bots } = useQuery({
    queryKey: ['bots-assignable'],
    // NOT `/bots`. That endpoint is scoped to AI Bots — correctly, because it
    // is the CRUD behind the Bots screen and a soundboard opened there gets an
    // edit form full of settings that do nothing to it. But it was also
    // feeding this dropdown, so a Soundboard Bot could be built, given clips
    // and switched on for a client, and then never selected here: the list
    // simply did not mention it.
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; code: string; kind: string }> }>(
        '/bot-assignments/assignable',
      ),
  });
  const { can } = useAuth();
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);
  const [transferTest, setTransferTest] = useState<TransferTest | null>(null);
  const [lastTest, setLastTest] = useState<TestSyncResult | null>(null);

  const testAll = useMutation({
    mutationFn: () => api.post<{ data: PingResult[]; ok: boolean; count: number }>('/vicidial/test-all', {}),
    onSuccess: (result) => {
      if (result.count === 0) {
        setToast({ message: 'No enabled Vicidial servers to test.', tone: 'bad' });
        return;
      }
      const failed = result.data.filter((entry) => !entry.ok);
      setToast({
        message: failed.length
          ? `${failed.length} of ${result.count} failed: ${failed
              .map((entry) => `${entry.integration ?? 'unnamed'} — ${entry.error ?? 'no response'}`)
              .join('; ')}`
          : `All ${result.count} Vicidial servers responded.`,
        tone: failed.length ? 'bad' : 'good',
      });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Connection test failed'),
        tone: 'bad',
      }),
  });

  // Test sync, not import. Importing pulled the dialer's agent list in and
  // created rows; what an operator actually needs to know is whether this
  // dialer answers AND whether its agents can log in — the second decides
  // whether a call ever reaches a bot, and it used to be invisible.
  // Read-only, and places no call. Every transfer fault seen so far is
  // visible without one: a dialplan never regenerated, an extension missing
  // because Apply was not pressed, a trunk the dialer is not answering on.
  const testTransfer = useMutation({
    mutationFn: (id: string) =>
      api.post<TransferTest>(`/vicidial-integrations/${id}/test-transfer`, {}),
    onSuccess: (result) => setTransferTest(result),
    onError: (err) =>
      setTransferTest({
        ok: false,
        ready: false,
        checks: [
          {
            name: 'Switchboard',
            passed: false,
            detail: errorMessage(err, 'Could not reach Asterisk'),
          },
        ],
      }),
  });

  // The running release, so a dialplan generated by an older one is spotted
  // here rather than on a live call.
  const { data: health } = useQuery({
    queryKey: ['platform-version'],
    queryFn: () => api.get<{ version?: string }>('/health'),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const platformVersion = health?.version ?? null;

  const testSync = useMutation({
    mutationFn: (integrationId: string) =>
      api.post<TestSyncResult>(`/vicidial-integrations/${integrationId}/test-sync`, {}),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-integrations'] });
      queryClient.invalidateQueries({ queryKey: ['vicidial-users'] });
      setLastTest(result);
      setToast({
        message: result.ok
          ? `${result.integration}: connected, and all ${result.agents_ok} agent(s) can log in.`
          : !result.connection.ok
            ? `${result.integration}: ${result.connection.detail}`
            : `${result.integration}: connected, but ${result.agents_total - result.agents_ok} of ${result.agents_total} agent(s) cannot log in.`,
        tone: result.ok ? 'good' : 'bad',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Test failed'), tone: 'bad' }),
  });

  return (
    <>
      <CrudPage<Dialer>
        title="Vicidial servers"
        description="Every dialer this account talks to. Bots, agents and campaigns each belong to one."
        resource="settings"
        path="/vicidial-integrations"
        queryKey="vicidial-integrations"
        searchPlaceholder="Search servers…"
        emptyTitle="No Vicidial servers yet"
        emptyDescription="Add one per dialer. Ten bots on one server and ten on another is a normal setup."
        createLabel="Add Vicidial server"
        extraActions={
          can('settings:read') && (
            <Button variant="secondary" loading={testAll.isPending} onClick={() => testAll.mutate()}>
              Test all connections
            </Button>
          )
        }
        filters={[
          {
            key: 'enabled',
            label: 'Status',
            options: [
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' },
            ],
          },
        ]}
        fields={[
          {
            name: 'name',
            label: 'Name',
            required: true,
            placeholder: 'Dialer A',
            hint: 'How this server is referred to throughout the portal.',
          },
          {
            name: 'code',
            label: 'Dialplan code',
            required: true,
            placeholder: 'dialer-a',
            hint: 'Letters, numbers, dashes. Asterisk passes this as integration=<code>.',
          },
          {
            name: 'base_url',
            label: 'Base URL',
            required: true,
            placeholder: 'https://dialer-a.example.com',
          },
          { name: 'api_user', label: 'API user', placeholder: 'apiuser' },
          {
            name: 'api_pass',
            label: 'API password',
            type: 'password',
            hint: 'Stored encrypted and never shown again.',
          },
          { name: 'source', label: 'Source', placeholder: 'gleeaibot' },

          // The Asterisk side. These were accepted by the API from the start
          // but appeared on no form anywhere, so `sip_host` could never be
          // filled in — which left every server at "Not configured", disabled
          // the Apply button, and made the whole trunk feature unreachable.
          {
            name: 'sip_host',
            label: 'Vicidial SIP host',
            placeholder: '49.12.133.2',
            hint:
              "The dialer's own IP or hostname, as Asterisk will reach it. " +
              'Required before this server can have a trunk — without it the ' +
              'Asterisk configuration cannot be generated and Apply stays off.',
          },
          {
            name: 'sip_port',
            label: 'SIP port',
            type: 'number',
            placeholder: '5060',
            hint: 'Leave blank for 5060.',
          },
          {
            name: 'remote_agent_ext',
            label: 'Remote Agent extension',
            placeholder: '13002',
            hint:
              'The extension Vicidial dials to reach a bot. It must match the ' +
              "Remote Agent entry on the dialer, and it is what this server's " +
              'dialplan context answers on.',
          },
          {
            name: 'transfer_method',
            label: 'Transfer method',
            type: 'select',
            options: [
              { value: 'AUTO', label: 'Automatic - use whichever is configured' },
              { value: 'REFER', label: 'SIP REFER to the Closer DID (frees the agent line)' },
              { value: 'EXTENSION', label: 'Dialplan extension' },
              { value: 'INGROUP', label: 'Vicidial in-group' },
              { value: 'DID', label: 'Dial the Closer DID' },
            ],
            hint:
              'How a qualified call leaves the BOT, and the only place it is ' +
              'set. Note that the dialer sees the same thing either way — ' +
              'an inbound call to the Closer DID — because a DID is the ' +
              'only address Vicidial publishes for inbound calls on a trunk. ' +
              'The methods differ in who holds the call afterwards. With SIP ' +
              'REFER, Vicidial moves its own customer to the DID and drops ' +
              'this platform, so the Remote Agent line is free. The extension ' +
              'and DID methods dial the closer FROM the bot leg, so the call ' +
              'runs through this server and the agent line stays busy until ' +
              'the closer hangs up. In-group needs a real Vicidial agent ' +
              'session, which an AI leg does not have.',
          },
          {
            name: 'blind_refer',
            label: 'Blind transfers by SIP REFER',
            type: 'checkbox',
            defaultValue: true,
            hint:
              'Recommended. A bot set to hand over BLIND asks Vicidial to move ' +
              'the customer to the Closer DID itself, so this platform drops ' +
              'out and the Remote Agent line is free at once -- no 3-WAY, no ' +
              'agent held for the whole closer call. Needs the Vicidial SIP ' +
              'host and the Closer DID. If the dialer refuses the REFER, the ' +
              'call falls back to the transfer extension on its own; untick ' +
              'only for a dialer that always refuses.',
          },
          {
            name: 'refer_number_did',
            label: 'Real-number DID (optional)',
            placeholder: '7861003',
            hint:
              'Leave empty unless closers should see the customer’s real ' +
              'number. A REFER moves Vicidial’s own call, which shows ' +
              'Vicidial’s call id instead. With a DID here, blind ' +
              'transfers go to <this DID>*<customer number>; a short block ' +
              'in the dialer’s trunkinbound context (see the Remote Agent ' +
              'guide) sets the real caller ID and routes the call to this ' +
              'DID. Give the DID a CIDLOOKUP call handle method so the ' +
              'closer gets the original lead. Press Apply after changing it.',
          },
          {
            name: 'closer_did',
            label: 'Closer DID',
            placeholder: '12255550101',
            hint:
              'The number the transfer dials back into this dialer. Vicidial ' +
              'sees an inbound call to it and routes it by its DID entry, so ' +
              'that entry must be active, routed to an in-group, and that ' +
              'in-group must have an agent logged in. Passed through ' +
              'verbatim, so a non-DID number the dialer answers on works too.',
          },
          {
            name: 'transfer_extension',
            label: 'Transfer extension',
            placeholder: '7861001',
            hint:
              'Generated into the BOT server’s dialplan, not the ' +
              'dialer’s — do not add it on Vicidial. Glee sends the ' +
              'customer here once the lead is qualified, and it dials the ' +
              'Closer DID back over this trunk — the same shape as a ' +
              'Vicidial fronter-to-closer extension. Preferred over dialling ' +
              'the DID directly, because handing the channel to the dialplan ' +
              'also takes the bot out of the call. Needs the Closer DID set, ' +
              'and an Apply afterwards.',
          },
          {
            name: 'transfer_trunk',
            label: 'Transfer trunk',
            placeholder: 'leave empty for this server’s own trunk',
            hint:
              'Which SIP trunk the transfer dials out over. Empty means this ' +
              'server’s own generated endpoint, which is what almost ' +
              'everyone wants — the call goes back to the dialer it came ' +
              'from. A bare name is treated as a PJSIP endpoint, so ' +
              '“my-carrier” dials PJSIP/<DID>@my-carrier. A value ' +
              'containing a slash is used as a complete dial string, so ' +
              '“SIP/carrier1” dials SIP/carrier1/<DID> — for a ' +
              'provider that is not a PJSIP endpoint here.',
          },
          {
            name: 'closer_in_group',
            label: 'Closer in-group',
            placeholder: 'VERIFIER01',
            hint: "The dialer's in-group that answers transferred calls. Optional.",
          },

          {
            name: 'default_bot_id',
            label: 'Default bot',
            type: 'select',
            options: (bots?.data ?? []).map((bot) => ({
              value: bot.id,
              // The kind, said in the option itself. Two bots called "Roofing"
              // that behave nothing alike, indistinguishable in a dropdown, is
              // the next version of this bug.
              label:
                bot.kind === 'SOUNDBOARD'
                  ? `${bot.name} (${bot.code}) — soundboard`
                  : `${bot.name} (${bot.code})`,
            })),
            hint:
              'Answers any call on this trunk that matched no assignment, no ' +
              "agent primary bot and no campaign default. Checked last, so it " +
              'never overrides routing you set deliberately. Set this if your ' +
              'dialer sends VDAD as the agent — no per-agent assignment can ' +
              'match Vicidial’s auto-dial pseudo-user.',
          },
          {
            name: 'verify_tls',
            label: 'Verify TLS certificate',
            type: 'checkbox',
            defaultValue: true,
            hint:
              'Turn off only for a dialer with a self-signed certificate or a ' +
              'missing intermediate chain, and only on a trusted network — an ' +
              'unverified connection can be intercepted.',
          },
          {
            name: 'mock_mode',
            label: 'Mock mode',
            type: 'checkbox',
            hint: 'Accept and log write-backs without contacting the dialer.',
          },
          { name: 'enabled', label: 'Enabled', type: 'checkbox', defaultValue: true },
        ]}
        columns={[
          {
            key: 'name',
            header: 'Server',
            render: (dialer) => (
              <div>
                <div className="text-sm text-ink-900 dark:text-ink-50">{dialer.name}</div>
                <div className="font-mono text-xs text-ink-500">{dialer.code ?? '—'}</div>
              </div>
            ),
          },
          {
            key: 'base_url',
            header: 'Base URL',
            render: (dialer) => (
              <span className="font-mono text-xs text-ink-600 dark:text-ink-300">
                {dialer.base_url}
              </span>
            ),
          },
          {
            key: 'default_bot',
            header: 'Default bot',
            // Visible because its absence is the failure: a trunk with no
            // default and a dialer that sends VDAD answers nothing, and the
            // only symptom is a call that never appears.
            render: (dialer) => {
              const bot = (bots?.data ?? []).find((b) => b.id === dialer.default_bot_id);
              return bot ? (
                <Badge tone="blue">{bot.name}</Badge>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <Badge tone="amber">Not set</Badge>
                  <span className="text-2xs text-ink-500">
                    Calls matching no assignment are dropped
                  </span>
                </div>
              );
            },
          },
          {
            key: 'connection',
            header: 'Connection',
            // "Enabled" only meant somebody ticked a box. This is the answer to
            // the question the list is actually being read for: does this
            // dialer answer, and can its agents log in?
            render: (dialer) => (
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap gap-1">
                  <Badge
                    tone={
                      dialer.connection_state === 'OK'
                        ? 'green'
                        : dialer.connection_state === 'FAILED'
                          ? 'red'
                          : dialer.connection_state === 'MOCK'
                            ? 'amber'
                            : 'gray'
                    }
                  >
                    {dialer.connection_state === 'OK'
                      ? 'Connected'
                      : dialer.connection_state === 'FAILED'
                        ? 'Not connected'
                        : dialer.connection_state === 'MOCK'
                          ? 'Mock — not contacted'
                          : 'Never tested'}
                  </Badge>
                  {dialer.connection_state !== 'UNKNOWN' &&
                    dialer.agents_total !== null &&
                    dialer.agents_total > 0 && (
                      <Badge
                        tone={
                          dialer.agents_ok === dialer.agents_total
                            ? 'green'
                            : dialer.agents_ok
                              ? 'amber'
                              : 'red'
                        }
                      >
                        {dialer.agents_ok ?? 0}/{dialer.agents_total} agents
                      </Badge>
                    )}
                </div>
                <span className="text-2xs text-ink-500">
                  {dialer.connection_state === 'UNKNOWN'
                    ? 'Run Test sync'
                    : `Checked ${formatRelative(dialer.connection_checked_at)}`}
                </span>
              </div>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (dialer) => (
              <div className="flex gap-1">
                <Badge tone={dialer.enabled ? 'green' : 'gray'}>
                  {dialer.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
                {dialer.mock_mode && <Badge tone="amber">Mock</Badge>}
              </div>
            ),
          },
          {
            key: 'asterisk',
            header: 'Asterisk',
            // Saving a server does not touch the switchboard — applying is a
            // separate, deliberate step. Without this column that step was
            // invisible here, so a server could look completely configured
            // while /etc/asterisk/glee/pjsip.d/ stayed empty and no call could
            // ever arrive.
            render: (dialer) => (
              <div className="flex flex-col gap-1">
                <AsteriskStateBadge state={dialer.asterisk_state} />
                <span className="text-2xs text-ink-500">
                  {dialer.asterisk_error ??
                    ASTERISK_STATE_HELP[dialer.asterisk_state] ??
                    ''}
                </span>
                {dialer.asterisk_state !== 'APPLIED' && (
                  <span className="text-2xs text-ink-400">
                    Applied from the client profile.
                  </span>
                )}
                {dialer.asterisk_state === 'APPLIED' &&
                  platformVersion &&
                  dialer.asterisk_applied_version !== platformVersion && (
                    <span className="text-2xs text-amber-700 dark:text-amber-400">
                      Generated by {dialer.asterisk_applied_version ?? 'an older release'}
                      {'; '}this platform is {platformVersion}. Releases change what the
                      dialplan does, and an upgrade does not re-apply it. Press Apply.
                    </span>
                  )}
              </div>
            ),
          },
          {
            key: 'sync',
            header: 'Last error',
            render: (dialer) => {
              const problem = dialer.connection_error ?? dialer.last_error;
              return problem ? (
                <span className="text-xs text-red-600 dark:text-red-400">{problem}</span>
              ) : (
                <span className="text-xs text-ink-500">—</span>
              );
            },
          },
          {
            key: 'actions',
            header: '',
            render: (dialer) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  loading={testSync.isPending && testSync.variables === dialer.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    testSync.mutate(dialer.id);
                  }}
                >
                  Test sync
                </Button>
                <Button
                  variant="ghost"
                  loading={
                    testTransfer.isPending && testTransfer.variables === dialer.id
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    testTransfer.mutate(dialer.id);
                  }}
                >
                  Test transfer
                </Button>
              </div>
            ),
          },
        ]}
      >
        <Card className="mb-4">
          <p className="text-sm text-ink-600 dark:text-ink-300">
            A Vicidial user, campaign id and lead id are only unique within one server, so every
            agent and campaign belongs to exactly one dialer here. That ownership is what decides
            where a call&rsquo;s disposition is written back and which DID a transfer goes out over
            — so a bot serving two dialers still hands each customer back to the one they came from.
          </p>
          <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
            Do-not-call requests are the deliberate exception: they are mirrored to{' '}
            <strong>every</strong> server, because someone who asked one call centre to stop has
            not agreed to be called from the other.
          </p>
        </Card>
      </CrudPage>

      {transferTest && (
        <Modal
          open
          onClose={() => setTransferTest(null)}
          wide
          title="Test transfer"
          footer={
            <Button variant="secondary" onClick={() => setTransferTest(null)}>
              Close
            </Button>
          }
        >
          <div className="space-y-3">
            <div
              className={
                transferTest.ready
                  ? 'rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200'
                  : 'rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300'
              }
            >
              <strong className="font-semibold">
                {transferTest.ready
                  ? 'This side is ready'
                  : 'A transfer would not get out of here'}
              </strong>
            </div>

            <ul className="divide-y divide-line text-sm">
              {transferTest.checks.map((check) => (
                <li key={check.name} className="flex gap-3 py-2">
                  <span className="w-5 shrink-0 text-center">
                    {check.passed === true && (
                      <span className="text-emerald-600">✓</span>
                    )}
                    {check.passed === false && <span className="text-red-600">✕</span>}
                    {check.passed === null && <span className="text-ink-400">–</span>}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium">{check.name}</span>
                    <span className="block text-xs text-ink-600 dark:text-ink-300">
                      {check.detail}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            {transferTest.note && (
              <p className="rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
                {transferTest.note}
              </p>
            )}
          </div>
        </Modal>
      )}

      {lastTest && (
        <Modal
          open
          onClose={() => setLastTest(null)}
          wide
          title={`Test sync — ${lastTest.integration ?? 'server'}`}
          footer={
            <Button variant="secondary" onClick={() => setLastTest(null)}>
              Close
            </Button>
          }
        >
          <div className="space-y-4">
            <div
              className={
                lastTest.connection.ok
                  ? 'rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200'
                  : 'rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300'
              }
            >
              <strong className="font-semibold">
                {lastTest.connection.ok ? 'Dialer reachable' : 'Dialer unreachable'}
              </strong>
              <span className="block">{lastTest.connection.detail}</span>
              {lastTest.connection.mock && (
                <span className="mt-1 block text-xs">
                  This server is in mock mode, so nothing was contacted.
                </span>
              )}
            </div>

            {lastTest.agents_total === 0 ? (
              <p className="text-sm text-ink-600 dark:text-ink-300">
                No agents are registered against this server yet. Add them with
                Bulk add agents, including their Vicidial passwords.
              </p>
            ) : (
              <>
                <p className="text-sm text-ink-600 dark:text-ink-300">
                  {lastTest.agents_ok} of {lastTest.agents_total} agent(s) can log
                  in. An agent that cannot produces silence on a call, not an
                  error, so it is worth fixing before going live.
                </p>
                <AgentLoginList agents={lastTest.agents} />
              </>
            )}
          </div>
        </Modal>
      )}

      <ExtensionRoutes bots={bots?.data ?? []} onToast={setToast} />

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

interface ExtensionRoute {
  id: string;
  integration_id: string;
  extension: string;
  bot_id: string;
  enabled: boolean;
  note: string | null;
  bot_name: string;
  bot_code: string;
  bot_kind: string;
  integration_name: string;
}

/**
 * Several bots on one Vicidial server, told apart by the extension dialled.
 *
 * This is the answer to "can I run two bots off one dialer?". It was already
 * possible and it was bookkeeping — assign every Remote Agent user to a bot,
 * and every new one as the campaign grows. The extension is the thing an
 * operator already thinks in, and it is decided once, when the Remote Agent
 * group is created: 13002 is the roofing bot, 13003 is the solar soundboard.
 *
 * TWO THINGS HAVE TO HAPPEN AND SOMEBODY WILL OTHERWISE DO ONE OF THEM: the
 * route has to exist, and the dialplan has to answer on the extension. A route
 * with no dialplan block is the worst outcome available — the call arrives,
 * hits the catch-all and is hung up, while this page says it is configured —
 * so it is said on the form rather than left to be discovered on a live call.
 */
function ExtensionRoutes({
  bots,
  onToast,
}: {
  bots: Array<{ id: string; name: string; code: string; kind: string }>;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [integrationId, setIntegrationId] = useState('');
  const [extension, setExtension] = useState('');
  const [botId, setBotId] = useState('');

  const routes = useQuery({
    queryKey: ['extension-routes'],
    queryFn: () => api.get<{ data: ExtensionRoute[] }>('/bot-assignments/extensions'),
  });
  const { data: dialers } = useDialers();

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['extension-routes'] });

  const add = useMutation({
    mutationFn: () =>
      api.post('/bot-assignments/extensions', {
        integration_id: integrationId,
        extension: extension.trim(),
        bot_id: botId,
      }),
    onSuccess: () => {
      onToast({
        message: 'Routed. Press Apply on that server so Asterisk answers on it.',
        tone: 'good',
      });
      setExtension('');
      refresh();
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not add'), tone: 'bad' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/bot-assignments/extensions/${id}`),
    onSuccess: () => {
      onToast({ message: 'Removed. Re-apply the dialplan on that server.', tone: 'good' });
      refresh();
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not remove'), tone: 'bad' }),
  });

  const rows = routes.data?.data ?? [];
  const editable = can('bot_assignments:create');
  const field =
    'w-full rounded-md border border-line bg-transparent px-2 py-1.5 text-sm';

  return (
    <Card
      title="Bots by extension"
      description="Run several bots on one Vicidial server. Each Remote Agent group has its own External Extension; point each extension at a bot."
      className="mt-4"
    >
      {editable && (
        <div className="mb-4 grid gap-3 rounded-md border border-line p-3 sm:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-500">Vicidial server</span>
            <select
              className={field}
              value={integrationId}
              onChange={(e) => setIntegrationId(e.target.value)}
            >
              <option value="">Choose…</option>
              {(dialers?.data ?? []).map((dialer: Dialer) => (
                <option key={dialer.id} value={dialer.id}>
                  {dialer.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-500">Extension</span>
            <input
              className={field}
              value={extension}
              placeholder="13003"
              onChange={(e) => setExtension(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-500">Answers with</span>
            <select className={field} value={botId} onChange={(e) => setBotId(e.target.value)}>
              <option value="">Choose…</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.kind === 'SOUNDBOARD'
                    ? `${bot.name} (${bot.code}) — soundboard`
                    : `${bot.name} (${bot.code})`}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <Button
              loading={add.isPending}
              disabled={!integrationId || !extension.trim() || !botId}
              onClick={() => add.mutate()}
            >
              Route it
            </Button>
          </div>
          <p className="text-xs text-ink-500 sm:col-span-4">
            This is the <strong>External Extension</strong> of a Remote Agent group
            in Vicidial. Add the group there with its own extension, route it here,
            then press <strong>Apply</strong> on that server so Asterisk answers on
            it — a route with no dialplan block is a call that gets hung up.
          </p>
        </div>
      )}

      {routes.isPending ? (
        <p className="text-sm text-ink-500">Reading routes…</p>
      ) : !rows.length ? (
        <p className="text-sm text-ink-500">
          Nothing routed by extension. Calls are matched by assignment, then the
          agent&apos;s primary bot, then this, then the campaign default, then the
          server&apos;s default bot.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="glee-table glee-table--compact">
            <thead>
              <tr>
                <th>Extension</th>
                <th>Answers with</th>
                <th>Server</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((route) => (
                <tr key={route.id}>
                  <td className="font-mono">{route.extension}</td>
                  <td>
                    {route.bot_name}{' '}
                    {route.bot_kind === 'SOUNDBOARD' && <Badge tone="blue">soundboard</Badge>}
                    {!route.enabled && <Badge tone="gray">paused</Badge>}
                  </td>
                  <td className="text-ink-500">{route.integration_name}</td>
                  <td className="text-right">
                    {editable && (
                      <Button variant="ghost" size="sm" onClick={() => remove.mutate(route.id)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
